import { type ReactNode } from "react";
import { type TFunction } from "i18next";

import { splitAggregatorIds } from "@/components/AggregatorAreaFilter";
import { FilterDefinition, FilterOption } from "@/components/FilterBar";

import { QUALITIES } from "../../constants";
import { getLocalisedName, translateOptions } from "../../utils/i18n";
import { OfferStatus } from "./components/OfferCard/interfaces";
import { OfferFilterKey, OfferTab } from "./types";
import { offerStatusLabelKey, tabStatuses } from "./utils";

export interface OfferFilterSources {
  tab: OfferTab;
  commodities: { id: number; name: string }[];
  // "Aggregator and Admin Area" options (value = aggregator id), see `AggregatorAreaFilter`
  aggregatorOptions: FilterOption[];
  renderAggregatorFilter: (id: string) => ReactNode;
}

// ExchangeAgreed is intentionally absent: it was never offered as a filter.
const FILTERABLE_STATUSES = [
  OfferStatus.Accepted,
  OfferStatus.ExchangeDone,
  OfferStatus.New,
  OfferStatus.Negotiating,
  OfferStatus.Recalled,
  OfferStatus.Rejected,
  OfferStatus.OfferDone,
  OfferStatus.ExchangeDetailsUpdated,
  OfferStatus.ExchangeDetailsDisputed,
  OfferStatus.ExchangePaymentPending,
  OfferStatus.ExchangeCanceled,
];

export const buildOfferFilterDefinitions = (
  t: TFunction,
  { tab, commodities, aggregatorOptions, renderAggregatorFilter }: OfferFilterSources
): FilterDefinition<OfferFilterKey>[] => [
  {
    key: "aggregator_ids",
    label: t("filters.aggregatorArea"),
    options: aggregatorOptions,
    render: renderAggregatorFilter,
    formatValue: value => {
      const ids = splitAggregatorIds(value);
      return ids.length === 1
        ? (aggregatorOptions.find(option => option.value === ids[0])?.label ?? ids[0])
        : t("multiselect.default.selectedCount", { count: ids.length });
    },
  },
  {
    key: "status",
    label: t("filters.status"),
    searchable: true,
    options: FILTERABLE_STATUSES.filter(status => tabStatuses(tab).includes(status)).map(
      status => ({
        value: status,
        label: t(offerStatusLabelKey(status)),
      })
    ),
  },
  {
    key: "commodity_id",
    label: t("filters.commodity"),
    searchable: true,
    options: (commodities || []).map(commodity => ({
      value: commodity.id.toString(),
      label: getLocalisedName(commodity),
    })),
  },
  {
    key: "quality",
    label: t("filters.quality"),
    options: translateOptions(QUALITIES, t).map((option: FilterOption) => ({
      value: option.value,
      label: option.label,
    })),
  },
];
